import { getReferrerAttribution } from "@outschool/attribution";
import { US_STATES } from "@outschool/business-rules";
import {
  esaUtmCampaigns,
  FundingProgramAbbreviation,
} from "@outschool/funding-programs-shared";
import { useLookupIP } from "@outschool/iplookup-client";
import { onboardingQuizPath } from "@outschool/routes";
import { useLocalStorageReference } from "@outschool/local-storage";
import { useSessionStorageState } from "@outschool/session-storage";
import { useQuery } from "@outschool/ui-apollo";
import { useSession } from "./SessionProvider";
import { SelfElectedStateFundingProgram } from "../lib/useSelfElectedStateFundingProgram";
import { fundingProgramPropertiesQuery } from "../graphql/FundingProgramPropertiesQuery";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useLocation } from "react-router-dom";
import { omit } from "lodash";
import { useUpdateCurrentUserPublicFundingMutation } from "../graphql/UpdateCurrentUserPublicFundingMutation";

export type FundingProgramPropertiesData = {
  abbreviation: string;
  fundingState: string | null;
  isClassWallet: boolean;
  institution: string | null;
  programName: string;
  programNameLong: string;
  programNameAbbr: string;
  programUrl: string | null;
  programEndDate: string | null;
  waitPeriodDays: number;
  landingPagePath: string | null;
};

type FundingProgramPropertiesQueryResult = {
  fundingProgramProperties: FundingProgramPropertiesData[];
};

const NO_STATE_ESA_UTM_CAMPAIGNS = ["sufs", "sft"];
const TEST_ESA_UTM_CAMPAIGN = "esa_test";
const DEFAULT_ESA_UTM_FUNDING_PROGRAM = FundingProgramAbbreviation.AZ;

type EsaExperienceContextValue = {
  isEsaExperience: boolean;
  disablePFBanner: boolean;
  sessionStateFundingProgram: SelfElectedStateFundingProgram | null;
  createAccountVariables: () => SelfElectedStateFundingProgram | undefined;
  fundingPrograms: FundingProgramPropertiesData[];
  fundingProgramsLoading: boolean;
};

const EsaExperienceContext = createContext<EsaExperienceContextValue>({
  isEsaExperience: false,
  disablePFBanner: false,
  sessionStateFundingProgram: null,
  createAccountVariables: () => undefined,
  fundingPrograms: [],
  fundingProgramsLoading: true,
});

export function EsaExperienceProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { isInUSA } = useLookupIP();
  const location = useLocation();
  const referrerAttribution = getReferrerAttribution();
  const { isEsaSession, currentUser } = useSession();
  const { localStorage } = useLocalStorageReference();
  const { data: fundingProgramsData, loading: fundingProgramsLoading } =
    useQuery<FundingProgramPropertiesQueryResult>(
      fundingProgramPropertiesQuery,
    );
  const fundingPrograms = useMemo(
    () => fundingProgramsData?.fundingProgramProperties ?? [],
    [fundingProgramsData?.fundingProgramProperties],
  );
  const [updateCurrentUserPublicFunding] =
    useUpdateCurrentUserPublicFundingMutation();
  const [sessionStateFundingProgram, setSessionStateFundingProgram] =
    useSessionStorageState<SelfElectedStateFundingProgram | null>(
      "sessionStateFundingProgram",
      null,
    );
  const isOnboardingQuizPage = location.pathname === onboardingQuizPath();

  const getSelfElectedStateFundingProgram = useCallback(() => {
    const storedValue = localStorage?.getItem(
      "self-elected-state-funding-program",
    );
    if (storedValue) {
      return JSON.parse(storedValue);
    }
    return null;
  }, [localStorage]);

  const isEsaUtmCampaign = useMemo(() => {
    if (!referrerAttribution || !referrerAttribution.utm_campaign) {
      return false;
    }
    const utmCampaign = !!esaUtmCampaigns.find(
      campaign =>
        campaign.toLowerCase() ===
        referrerAttribution.utm_campaign.toLowerCase(),
    );

    if (!utmCampaign) {
      return referrerAttribution.utm_campaign.toLowerCase().includes("esa");
    }

    return utmCampaign;
  }, [referrerAttribution]);

  const fundingProgram = useMemo(() => {
    if (isEsaUtmCampaign) {
      const landingPage = referrerAttribution?.landingPage;
      const utmCampaign =
        referrerAttribution?.utm_campaign?.toLowerCase() || "";
      const matchedByPath = landingPage
        ? fundingPrograms.find(
            p => p.landingPagePath && landingPage.includes(p.landingPagePath),
          )
        : undefined;
      const isNoStateFundingProgram =
        NO_STATE_ESA_UTM_CAMPAIGNS.includes(utmCampaign) ||
        (utmCampaign.includes("esa") && utmCampaign !== TEST_ESA_UTM_CAMPAIGN);
      if (matchedByPath) {
        return matchedByPath;
      } else if (!isNoStateFundingProgram && sessionStateFundingProgram) {
        return (
          fundingPrograms.find(
            p =>
              p.abbreviation === sessionStateFundingProgram.stateAbbreviation,
          ) ?? null
        );
      } else if (!isNoStateFundingProgram) {
        return (
          fundingPrograms.find(
            p => p.abbreviation === DEFAULT_ESA_UTM_FUNDING_PROGRAM,
          ) ?? null
        );
      }
    }
    return null;
  }, [
    isEsaUtmCampaign,
    sessionStateFundingProgram,
    fundingPrograms,
    referrerAttribution?.utm_campaign,
    referrerAttribution?.landingPage,
  ]);

  const isEsaExperience = useMemo(() => {
    if (isEsaSession) {
      return true;
    }

    const selfElectedStateFundingProgram = getSelfElectedStateFundingProgram();

    const fundingOptions = sessionStateFundingProgram
      ? sessionStateFundingProgram.fundingOptions
      : selfElectedStateFundingProgram?.fundingOptions;

    const isValidFundingOption = fundingOptions
      ? Object.values(fundingOptions).some(option => option)
      : false;

    if (isValidFundingOption) {
      return true;
    }

    if (
      currentUser &&
      currentUser.selfElectedStateFundingProgram?.stateAbbreviation
    ) {
      return true;
    }

    return isEsaUtmCampaign;
  }, [
    isEsaSession,
    isEsaUtmCampaign,
    sessionStateFundingProgram,
    currentUser,
    getSelfElectedStateFundingProgram,
  ]);

  const disablePFBanner = useMemo(() => {
    if (isEsaExperience) {
      return true;
    }

    if (currentUser) {
      return true;
    }

    if (!isInUSA) {
      return true;
    }

    if (isOnboardingQuizPage) {
      return true;
    }

    const selfElectedStateFundingProgram = getSelfElectedStateFundingProgram();

    return selfElectedStateFundingProgram?.hideBanner || false;
  }, [
    isEsaExperience,
    currentUser,
    isInUSA,
    isOnboardingQuizPage,
    getSelfElectedStateFundingProgram,
  ]);

  useEffect(() => {
    if (isEsaUtmCampaign) {
      const fundingStateAbbreviation =
        fundingProgram?.fundingState ?? fundingProgram?.abbreviation ?? "";
      const [stateAbbreviation, stateName] = fundingProgram
        ? [
            fundingStateAbbreviation,
            US_STATES.find(
              state => state.abbreviation === fundingStateAbbreviation,
            )?.name || "",
          ]
        : ["", ""];
      setSessionStateFundingProgram({
        stateAbbreviation,
        stateName,
        source: referrerAttribution?.utm_campaign.toLowerCase() || "",
        fundingOptions: {
          charter: false,
          statePortal: false,
          otherProgramName: "",
          reimbursement: false,
          selfPay: true,
          stateDebitCard: false,
          stateProgramName: fundingProgram
            ? fundingProgram.abbreviation
            : "default",
        },
      });
    }
  }, [
    isEsaUtmCampaign,
    referrerAttribution?.utm_campaign,
    setSessionStateFundingProgram,
    fundingProgram,
  ]);

  useEffect(() => {
    const userFundingOption = currentUser?.selfElectedStateFundingProgram
      ?.fundingOptions
      ? Object.values(
          omit(
            currentUser.selfElectedStateFundingProgram.fundingOptions,
            "__typename",
          ),
        ).some(fp => fp)
      : false;
    if (currentUser && !userFundingOption && sessionStateFundingProgram) {
      updateCurrentUserPublicFunding({
        variables: {
          details: {
            selfElectedStateFundingProgram: sessionStateFundingProgram,
          },
        },
      });
    }
  }, [sessionStateFundingProgram, currentUser, updateCurrentUserPublicFunding]);

  const createAccountVariables = useCallback(() => {
    if (sessionStateFundingProgram) {
      return sessionStateFundingProgram;
    }

    const selfElectedStateFundingProgram = getSelfElectedStateFundingProgram();

    if (
      selfElectedStateFundingProgram &&
      !selfElectedStateFundingProgram.hasRejectedStateFundingQuestions &&
      selfElectedStateFundingProgram.stateAbbreviation !== ""
    ) {
      return omit(
        selfElectedStateFundingProgram,
        "searchFiltersActive",
        "hideBanner",
        "hasRejectedStateFundingQuestions",
      ) as SelfElectedStateFundingProgram;
    }

    return undefined;
  }, [sessionStateFundingProgram, getSelfElectedStateFundingProgram]);

  return (
    <EsaExperienceContext.Provider
      value={{
        isEsaExperience,
        disablePFBanner,
        sessionStateFundingProgram,
        createAccountVariables,
        fundingPrograms,
        fundingProgramsLoading,
      }}
    >
      {children}
    </EsaExperienceContext.Provider>
  );
}

export function useEsaExperience() {
  const context = useContext(EsaExperienceContext);

  if (context === undefined) {
    throw new Error(
      "useEsaExperience must be used within an EsaExperienceProvider",
    );
  }

  return context;
}
